import React from "react";

import { Ds, Feedback } from "@accurx/design";
import { i } from "images";

import { UpdatingStatusType } from "shared/LoadingStatus";

import { Settings } from "../../constants";
import { PatientTriageStatus } from "../../types";
import { UrgentHelpSection } from "../UrgentHelpSection/UrgentHelpSection";

export type ChangeOohPageFormPropsType = {
    status: PatientTriageStatus;
    triageSettingsUpdatingStatus: UpdatingStatusType;
    hasClickedEditOohPage: boolean;
    defaultOohText: string;
    outOfHoursPageMessage: string | null;
    handleUpdateCustomCopy: (
        event:
            | React.MouseEvent<HTMLButtonElement>
            | React.FormEvent<HTMLFormElement>,
        name: string,
        resetToDefault: boolean | null,
    ) => void;

    handleOohPageMessageCancel: () => void;
    hasClickedEditButton: (
        event: React.MouseEvent<HTMLButtonElement>,
        name: string,
    ) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    handleUpdateString: (event: any, name: string) => void;
    handleAccordionClick: (name: string) => void;
};

export const ChangeOohPageForm = ({
    status,
    triageSettingsUpdatingStatus,
    defaultOohText,
    outOfHoursPageMessage,
    hasClickedEditOohPage,
    handleOohPageMessageCancel,
    hasClickedEditButton,
    handleUpdateCustomCopy,
    handleUpdateString,
    handleAccordionClick,
}: ChangeOohPageFormPropsType): JSX.Element => {
    const isDisabled =
        status.isAdmin === false || triageSettingsUpdatingStatus.updating;

    return (
        <form
            onSubmit={(e): void =>
                handleUpdateCustomCopy(e, Settings.TEXT_OOH_PAGE, false)
            }
        >
            <Ds.Card style={{ marginBottom: "1rem" }} appearance="elevated">
                <Ds.Card.Header>
                    <Ds.Text as="h3" weight="bold" size="large">
                        Edit out of hours message
                    </Ds.Text>
                </Ds.Card.Header>
                <Ds.Card.Body>
                    <Ds.Text mb="2">
                        This is the message at the top of the out of hours page.
                        The message is only available during out of hours.
                    </Ds.Text>

                    <Ds.Accordion
                        onValueChange={() =>
                            handleAccordionClick(Settings.TEXT_OOH_PAGE)
                        }
                    >
                        <Ds.Accordion.Item value="OOH-page">
                            <Ds.Accordion.Trigger>
                                <Ds.Flex gap="2">
                                    <Ds.Flex.Item>
                                        <Ds.Icon
                                            name="Photo"
                                            color="night"
                                            size="small"
                                        />
                                    </Ds.Flex.Item>
                                    <Ds.Flex.Item>
                                        <Ds.Text weight="bold">
                                            See where this is on the patient
                                            form
                                        </Ds.Text>
                                    </Ds.Flex.Item>
                                </Ds.Flex>
                            </Ds.Accordion.Trigger>
                            <Ds.Accordion.Content>
                                <img
                                    width="100%"
                                    src={i(
                                        "patientTriage/outofhours-location.png",
                                    )}
                                    alt="Location of out of hours message in patient triage flow"
                                />
                            </Ds.Accordion.Content>
                        </Ds.Accordion.Item>
                    </Ds.Accordion>

                    <Ds.Flex flexDirection="column" gap="2" py="3">
                        <Feedback
                            colour="secondary"
                            title="When editing the message, remember to:"
                        >
                            <ul className="pl-3">
                                <li>
                                    remind there may be a delay in responding
                                    because of the time of day
                                </li>
                                <li>
                                    remind the patients that they can still
                                    submit a non-urgent request
                                </li>
                                <li>encourage to use other local services</li>
                                <li>not include any website links</li>
                            </ul>
                        </Feedback>
                    </Ds.Flex>

                    <div
                        className="p-2"
                        style={{
                            backgroundColor: `var(--ds-color-cloud)`,
                        }}
                    >
                        <Ds.Text weight="bold" mb="2">
                            Your current message:
                        </Ds.Text>

                        <Feedback colour="warning">
                            <>
                                {hasClickedEditOohPage ? (
                                    <Ds.Textarea
                                        autoResize
                                        rows={2}
                                        value={
                                            outOfHoursPageMessage ??
                                            status.outOfHoursPageMessage ??
                                            defaultOohText
                                        }
                                        onChange={(e) =>
                                            handleUpdateString(
                                                e,
                                                Settings.TEXT_OOH_PAGE,
                                            )
                                        }
                                        disabled={isDisabled}
                                        name="outOfHoursCopy"
                                    />
                                ) : (
                                    <Ds.Text>
                                        {outOfHoursPageMessage ??
                                            status.outOfHoursPageMessage ??
                                            defaultOohText}
                                    </Ds.Text>
                                )}

                                <UrgentHelpSection region={status.region} />
                            </>
                        </Feedback>
                    </div>
                </Ds.Card.Body>
                <Ds.Card.Footer>
                    <Ds.Flex justifyContent="flex-end">
                        {hasClickedEditOohPage ? (
                            <Ds.Flex gap="2">
                                <Ds.Button
                                    appearance="primary"
                                    data-testid="outOfHoursMessageSave"
                                    disabled={isDisabled}
                                    type="submit"
                                >
                                    Save Changes
                                </Ds.Button>
                                <Ds.Button
                                    appearance="secondary"
                                    onClick={(e): void =>
                                        handleUpdateCustomCopy(
                                            e,
                                            Settings.TEXT_OOH_PAGE,
                                            true,
                                        )
                                    }
                                    data-testid="outOfHoursMessageReset"
                                    disabled={isDisabled}
                                    type="button"
                                >
                                    Reset to default
                                </Ds.Button>
                                <Ds.Button
                                    size="medium"
                                    appearance="tertiary"
                                    onClick={handleOohPageMessageCancel}
                                    data-testid="outOfHoursMessageCancel"
                                    type="button"
                                >
                                    Cancel
                                </Ds.Button>
                            </Ds.Flex>
                        ) : (
                            <Ds.Button
                                onClick={(e): void =>
                                    hasClickedEditButton(
                                        e,
                                        Settings.TEXT_OOH_PAGE,
                                    )
                                }
                                disabled={isDisabled}
                                data-testid="outOfHoursMessageEdit"
                                type="button"
                            >
                                <Ds.Icon
                                    name="Pencil"
                                    color="white"
                                    size="small"
                                />
                                <Ds.Text color="white" weight="bold">
                                    Edit
                                </Ds.Text>
                            </Ds.Button>
                        )}{" "}
                    </Ds.Flex>
                </Ds.Card.Footer>
            </Ds.Card>
        </form>
    );
};
